/**
 * Client-side mirror of the manual rail's server validation.
 *
 * Every rule here has a twin in the api (`CreatePaymentInput.validate` and
 * `RecordManualPayment`, all raising `expert_payment.invalid_input`). This is
 * not the gate — the server is — it exists so an operator finds out before a
 * round trip, on a form where the round trip writes an immutable row.
 *
 * The bounds are mirrored deliberately, and where the api is inconsistent this
 * mirrors the inconsistency rather than tidying it: `externalRef` is bounded in
 * BYTES (`len()`) while the others are bounded in RUNES
 * (`utf8.RuneCountInString`). A rune check on a byte-bounded field would pass a
 * string the server then rejects, which is exactly the surprise this module
 * exists to prevent.
 */

export const PAYEE_ID_MAX = 36;
export const SOURCE_ID_MAX = 64;
export const DESCRIPTION_MAX = 255;
export const EXTERNAL_REF_MAX_BYTES = 255;

/** The earliest plausible effective payment date (api `paidAtFloor`). */
export const PAID_AT_FLOOR = new Date(Date.UTC(2010, 0, 1));
/** The api tolerates 24h of clock skew ahead of now, and no more. */
export const PAID_AT_SKEW_MS = 24 * 60 * 60 * 1000;

export interface ManualPaymentDraft {
  payeeUserId: string;
  sourceId: string;
  description: string;
  externalRef: string;
  /** Dollars, as typed. Converted to minor units only after it validates. */
  amount: string;
  /** `YYYY-MM-DD`, or empty for "now". */
  paidAt: string;
  reasoning: string;
}

export type ManualPaymentErrors = Partial<Record<keyof ManualPaymentDraft, string>>;

/** Runes, not UTF-16 code units — `String.length` counts a 😀 as 2. */
function runeCount(s: string): number {
  return [...s].length;
}

function byteCount(s: string): number {
  return new TextEncoder().encode(s).length;
}

/**
 * Dollars → minor units, or null if the input is not a well-formed amount.
 *
 * `Number.parseFloat` is deliberately not the test: it reads `'12abc'` as 12,
 * which on a payment form means an operator's typo silently becomes a real
 * amount. The pattern is the gate; the parse only runs once it passes.
 *
 * Rounding is applied to a value already constrained to two decimal places, so
 * it is exact — `19.99 * 100` comes out at 1998.9999… in binary floating point
 * and would truncate to 1998 without it.
 */
export function parseAmountToCents(amount: string): number | null {
  if (!/^\d+(\.\d{1,2})?$/.test(amount.trim())) return null;
  const cents = Math.round(Number.parseFloat(amount.trim()) * 100);
  return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
}

/** `YYYY-MM-DD` as a UTC instant — the tax year buckets on UTC. */
export function parsePaidAt(paidAt: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(paidAt)) return null;
  const [y, m, d] = paidAt.split('-').map(Number);
  const parsed = new Date(Date.UTC(y, m - 1, d));
  // Round-trip guard: Date.UTC happily rolls 2026-02-31 forward into March.
  if (
    parsed.getUTCFullYear() !== y ||
    parsed.getUTCMonth() !== m - 1 ||
    parsed.getUTCDate() !== d
  ) {
    return null;
  }
  return parsed;
}

export function validateManualPayment(
  draft: ManualPaymentDraft,
  now: Date = new Date()
): ManualPaymentErrors {
  const errors: ManualPaymentErrors = {};

  const payee = draft.payeeUserId.trim();
  if (!payee) {
    errors.payeeUserId = 'Required — the expert’s user id.';
  } else if (runeCount(payee) > PAYEE_ID_MAX) {
    errors.payeeUserId = `Must be at most ${PAYEE_ID_MAX} characters.`;
  }

  const sourceId = draft.sourceId.trim();
  if (!sourceId) {
    errors.sourceId = 'Required — the id of the consultation, response or submission paid for.';
  } else if (sourceId.includes(':')) {
    // live_marker is colon-joined; a colon here could collide two distinct
    // obligation keys on uniq_live_source_kind.
    errors.sourceId = 'Cannot contain a colon.';
  } else if (runeCount(sourceId) > SOURCE_ID_MAX) {
    errors.sourceId = `Must be at most ${SOURCE_ID_MAX} characters.`;
  }

  const description = draft.description.trim();
  if (!description) {
    errors.description = 'Required — this is the line item Finance reads and exports.';
  } else if (runeCount(description) > DESCRIPTION_MAX) {
    errors.description = `Must be at most ${DESCRIPTION_MAX} characters.`;
  }

  const externalRef = draft.externalRef.trim();
  if (!externalRef) {
    errors.externalRef = 'Required — the wire or check reference this payment can be traced by.';
  } else if (byteCount(externalRef) > EXTERNAL_REF_MAX_BYTES) {
    errors.externalRef = `Must be at most ${EXTERNAL_REF_MAX_BYTES} bytes.`;
  }

  if (parseAmountToCents(draft.amount) === null) {
    errors.amount = 'Enter a positive amount in dollars, e.g. 1250.00.';
  }

  if (draft.paidAt) {
    const paidAt = parsePaidAt(draft.paidAt);
    if (!paidAt) {
      errors.paidAt = 'Enter a real date as YYYY-MM-DD.';
    } else if (paidAt.getTime() > now.getTime() + PAID_AT_SKEW_MS) {
      errors.paidAt = 'Cannot be in the future.';
    } else if (paidAt.getTime() < PAID_AT_FLOOR.getTime()) {
      errors.paidAt = 'Cannot be before 2010-01-01.';
    }
  }

  return errors;
}

export function hasErrors(errors: ManualPaymentErrors): boolean {
  return Object.keys(errors).length > 0;
}
